import classnames from 'classnames';
import { Empty } from '@vyaguta/vyaguta-ui';
import { FiMoreVertical } from '@vyaguta/vyaguta-icons/fi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import UserDetail from './UserDetail';
import Table from 'components/common/table';
import { AddUserModal } from './AddUserModal';
import PopOver from 'components/common/popover';
import VerticalEllipsis from './VerticalEllipsis';
import Pagination from 'components/common/paginator';
import Loading from 'components/common/loading/Loading';
import UserPermissionsFilter from './UserPermissionsFilter';
import TableHeader from 'components/common/table/TableHeader';

import { interpolate } from 'utils/string';
import { getIndexOfSN } from 'utils/table';
import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { parse, stringify } from 'utils/queryParam';

import * as userService from 'services/user';

import useTitle from 'hooks/useTitle';

import { EMPLOYEE_PROFILE } from 'constants/routes';
import { USER_ROLES_EMPTY } from 'constants/employeeRoles';
import { USER_FETCH_FIELDS } from 'constants/appConstants';
import { DEFAULT_PAGE_SIZE, MINIMUM_PAGE_COUNT } from 'constants/page';
import { USER_SELECTED_FIELDS, MAKE_SEARCH_BY } from 'constants/fetchTypes';

import type { QueryObject } from 'interface/queryParam';
import type { PaginationMeta, QueryValue, SelectedUser, UserRoleRow } from './types';

type QueryParamRecord = Record<string, QueryValue>;
type UserPermissionsQuery = { roleIds?: QueryValue; size?: QueryValue };

const toStringValue = (value: QueryValue): string | undefined => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') return value[0];
  return undefined;
};

const toNumberValue = (value: QueryValue): number | undefined => {
  const raw = toStringValue(value);
  if (!raw) return undefined;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
};

type TableCellWithIndex = { index: number };
type TableCellWithRow<T> = { original: T };
type TableRowInfo<T> = { original: T };

type FetchAllResponse = {
  data: UserRoleRow[];
  meta: PaginationMeta;
};

type Props = RouteComponentProps & {
  updateFilterState?: (value: boolean) => void;
};

const UserPermissions: React.FC<Props> = (props) => {
  const { history } = props;
  const queryParamsRaw = parse(props.location.search) as QueryParamRecord;
  const queryParams = queryParamsRaw as UserPermissionsQuery;

  const [isFilterApplied, setIsFilterApplied] = useState(!isObjectEmpty(queryParamsRaw));
  const [userRoleData, setUserRoleData] = useState<UserRoleRow[]>([]);
  const [pageData, setPageData] = useState<PaginationMeta>({ page: 1, total: 0 });
  const [pageCount, setPageCount] = useState(0);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isRoleAdded, setIsRoleAdded] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(true);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

  const columnToSortBy = 'name';

  const defaultSorted = useMemo(() => [{ id: 'name', desc: false }], []);

  const getColumns = useCallback(
    (fetchUserRoles: () => Promise<void>, pageMeta: PaginationMeta, currentPageSize: number) => {
      const roleIdsCsv = toStringValue(queryParams.roleIds);
      const roleIds = roleIdsCsv ? roleIdsCsv.split(',') : [];

      return [
        {
          Header: <TableHeader label="SN." identifier="sn" />,
          accessor: 'sn',
          maxWidth: 56,
          sortable: false,
          filterable: false,
          Cell: (cell: TableCellWithIndex) => {
            const index = getIndexOfSN(cell, currentPageSize, pageMeta);
            return <span className="capital-text employees__table-text">{index < 10 ? `0${index}` : index}</span>;
          },
        },
        {
          Header: () => (
            <TableHeader
              label="Name"
              identifier="name"
              columnToSortBy={columnToSortBy}
              isAscendingOrdered={isAscendingOrdered}
              onClick={() => {
                setIsAscendingOrdered(!isAscendingOrdered);
              }}
            />
          ),
          accessor: 'name',
          maxWidth: 325,
          Cell: (cell: TableCellWithRow<UserRoleRow>) => {
            const { empId, firstName, lastName, designation, id, roles } = cell.original;
            return <UserDetail detail={{ empId, fullName: `${firstName} ${lastName}`, designation, id, roles }} />;
          },
        },
        {
          Header: <div className="d-flex align-items-center">Role(s)</div>,
          accessor: 'role',
          sortable: false,
          Cell: (cell: TableCellWithRow<UserRoleRow>) => {
            const { roles, id } = cell.original;

            return (
              <div className="employee__role" id={`user-role-${id}`}>
                {roles.length > 0
                  ? roles.map((role) => {
                      const matchId = roleIds.find((rId) => String(rId) === String(role.id));
                      return (
                        <span key={role.id} className={classnames('role-table-item', { 'text-bold': matchId })}>
                          {role.name}
                        </span>
                      );
                    })
                  : '-'}
              </div>
            );
          },
        },
        {
          Header: '',
          width: 80,
          id: 'more',
          sortable: false,
          Cell: (cell: TableCellWithRow<UserRoleRow>) => {
            const { empId, firstName, lastName, designation, id, roles } = cell.original;
            const selectedUser: SelectedUser = {
              empId,
              fullName: `${firstName} ${lastName}`,
              designation,
              id,
              roles,
            };

            return (
              <span
                className="vertical-ellipsis"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <PopOver
                  interactive
                  theme={'light'}
                  open={openPopoverId === id}
                  onRequestClose={() => setOpenPopoverId(null)}
                  size="regular"
                  customClass="overflow-menu__regular"
                  position="left-start"
                  distance={5}
                  disableOnScroll={true}
                  popperOptions={{
                    modifiers: {
                      addZIndex: {
                        enabled: true,
                        order: 810,
                        fn: (data: { styles: Record<string, string | number> }) => ({
                          ...data,
                          styles: {
                            ...data.styles,
                            zIndex: 10,
                          },
                        }),
                      },
                    },
                  }}
                  html={
                    <VerticalEllipsis
                      selectedUser={selectedUser}
                      fetchUserRoles={fetchUserRoles}
                      onClose={() => setOpenPopoverId(null)}
                    />
                  }
                  className="vertical-ellipsis__menu dots-menu"
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenPopoverId(openPopoverId === id ? null : id);
                    }}
                  >
                    <FiMoreVertical className="grey--text" size={24} />
                  </span>
                </PopOver>
              </span>
            );
          },
        },
      ];
    },
    [columnToSortBy, isAscendingOrdered, queryParams.roleIds, openPopoverId]
  );

  const setTitle = useTitle();

  useEffect(() => {
    setTitle('Role Management');
  }, [setTitle]);

  const updateUrl = useCallback(
    (value: QueryObject) => {
      const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });
      history.push({
        pathname: props.location.pathname,
        search: queryParam,
      });
    },
    [history, props.location.pathname]
  );

  const fetchUserRoles = useCallback(async () => {
    try {
      setIsDataLoading(true);

      const raw = parse(props.location.search) as QueryParamRecord;
      const params: QueryObject = {
        ...raw,
        fields: [USER_FETCH_FIELDS.ROLES, ...USER_SELECTED_FIELDS].join(','),
        searchBy: MAKE_SEARCH_BY.join(','),
      };

      const size = toNumberValue((raw as UserPermissionsQuery).size) || DEFAULT_PAGE_SIZE;
      setPageSize(size);

      const { data, meta } = (await userService.fetchAll(params)) as FetchAllResponse;
      setUserRoleData(data);
      setPageData(meta);

      const totalPage = Math.ceil(meta.total / size);
      setPageCount(totalPage);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDataLoading(false);
      setIsRoleAdded(false);
    }
  }, [props.location.search]);

  const columns = useMemo(
    () => getColumns(fetchUserRoles, pageData, pageSize),
    [getColumns, fetchUserRoles, pageData, pageSize]
  );

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        setIsDataLoading(true);

        const raw = parse(props.location.search) as QueryParamRecord;
        const params: QueryObject = {
          ...raw,
          fields: [USER_FETCH_FIELDS.ROLES, ...USER_SELECTED_FIELDS].join(','),
          searchBy: MAKE_SEARCH_BY.join(','),
        };

        const size = toNumberValue((raw as UserPermissionsQuery).size) || DEFAULT_PAGE_SIZE;
        setPageSize(size);

        const { data, meta } = (await userService.fetchAll(params)) as FetchAllResponse;
        setUserRoleData(data);
        setPageData(meta);

        const totalPage = Math.ceil(meta.total / size);
        setPageCount(totalPage);
      } catch (error) {
        handleError(error);
      } finally {
        setIsDataLoading(false);
        setIsRoleAdded(false);
      }
    };

    void loadUserRoles();
  }, [isRoleAdded, props.location.search, setIsRoleAdded]);

  const applyFilter = (filter: QueryObject) => {
    updateUrl(filter);
  };

  const onPageChange = useCallback(
    (selectedPage: number) => {
      const raw = parse(props.location.search) as QueryParamRecord;
      updateUrl({ ...raw, page: selectedPage });
    },
    [props.location.search, updateUrl]
  );

  const onPageSizeChange = useCallback(
    (selectedSize: number) => {
      setIsFilterApplied(true);
      const raw = parse(props.location.search) as QueryParamRecord;
      updateUrl({ ...raw, page: 1, size: selectedSize });
    },
    [props.location.search, updateUrl]
  );

  const handleAddUserClick = (state: boolean) => {
    setShowAddUserModal(state);
  };

  return (
    <>
      <div className="full-scope-card">
        <UserPermissionsFilter
          {...props}
          data={userRoleData}
          applyFilter={applyFilter}
          handleAddUserClick={handleAddUserClick}
          setIsFilterApplied={setIsFilterApplied}
          isFilterApplied={isFilterApplied}
        />
        <div className={classnames({ loading__container: isDataLoading })}>
          {isDataLoading ? (
            <Loading />
          ) : userRoleData && userRoleData.length ? (
            <>
              <Table
                data={userRoleData}
                defaultSorted={defaultSorted}
                columns={columns}
                showPagination={false}
                noDataText={USER_ROLES_EMPTY}
                getTrProps={(_state: object, rowInfo?: TableRowInfo<UserRoleRow>) => {
                  if (!rowInfo) {
                    return {};
                  }

                  return {
                    className: 'align-items-center d-flex hover-visible-dots',
                    onClick: (e: MouseEvent) => {
                      const url = interpolate(EMPLOYEE_PROFILE, { id: rowInfo.original.id });
                      const hasMeta = 'metaKey' in e ? Boolean((e as { metaKey?: boolean }).metaKey) : false;
                      const hasCtrl = 'ctrlKey' in e ? Boolean((e as { ctrlKey?: boolean }).ctrlKey) : false;

                      if (hasCtrl || hasMeta) {
                        window.open(url);
                      } else {
                        history.push(url);
                      }
                    },
                  };
                }}
              />
              <Pagination
                pageData={pageData}
                pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            </>
          ) : (
            <Empty message={USER_ROLES_EMPTY} />
          )}
        </div>
      </div>
      <div>
        {showAddUserModal ? (
          <AddUserModal setShowAddUserModal={handleAddUserClick} setIsRoleAdded={setIsRoleAdded} />
        ) : null}
      </div>
    </>
  );
};

export default UserPermissions;
